import React from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { Button } from './Button';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirmer',
  cancelText = 'Annuler',
  variant = 'warning',
}) => {
  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const variantStyles = {
    danger: 'text-red-600 dark:text-red-400',
    warning: 'text-orange-600 dark:text-orange-400',
    info: 'text-blue-600 dark:text-blue-400',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-md rounded-t-2xl sm:rounded-lg bg-white p-6 sm:p-6 shadow-xl dark:bg-gray-800 mx-auto sm:my-auto">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 touch-manipulation h-10 w-10 sm:h-auto sm:w-auto flex items-center justify-center"
        >
          <X className="h-6 w-6 sm:h-5 sm:w-5" />
        </button>

        <div className="mb-6 sm:mb-4 flex items-start gap-4 sm:gap-3">
          <div className={`rounded-full bg-gray-100 p-3 sm:p-2 dark:bg-gray-700 ${variantStyles[variant]}`}>
            <AlertTriangle className="h-7 w-7 sm:h-6 sm:w-6" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl sm:text-lg font-semibold text-gray-900 dark:text-white">
              {title}
            </h3>
            <p className="mt-2 text-base sm:text-sm text-gray-600 dark:text-gray-300">
              {message}
            </p>
          </div>
        </div>

        <div className="mt-6 flex flex-col-reverse sm:flex-row justify-end gap-3">
          <Button
            variant="secondary"
            onClick={onClose}
          >
            {cancelText}
          </Button>
          <Button
            variant={variant === 'danger' ? 'primary' : 'primary'}
            onClick={handleConfirm}
            className={
              variant === 'danger'
                ? '!bg-red-600 hover:!bg-red-700 dark:!bg-red-600 dark:hover:!bg-red-700'
                : ''
            }
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </div>
  );
};
